import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  FiTool, FiSettings, FiZap, FiBattery, 
  FiThermometer, FiDisc, FiActivity, FiCheckCircle 
} from 'react-icons/fi';
import { serviceCategories } from '../../../utils/htmlToReact';
import RelatedInventory from '../../../components/RelatedInventory';

const ServicesIndex = () => {
  const allServices = [
    {
      icon: FiTool,
      title: 'European Car Repair',
      description: 'Expert repair and maintenance for BMW, Mercedes, Audi, Porsche, and more',
      link: '/services/european-car-repair',
      color: 'bg-blue-500'
    },
    {
      icon: FiSettings,
      title: 'Factory Maintenance',
      description: 'Keep your warranty valid with scheduled maintenance',
      link: '/services/factory-maintenance',
      color: 'bg-yellow-500'
    },
    {
      icon: FiDisc,
      title: 'Suspension Repair',
      description: 'Shocks, struts, alignment for a smooth ride',
      link: '/services/suspension-repair',
      color: 'bg-red-500'
    },
    {
      icon: FiZap,
      title: 'Transmission Repair',
      description: 'Expert transmission diagnosis and repair services',
      link: '/services/transmission-repair',
      color: 'bg-purple-500'
    },
    {
      icon: FiThermometer,
      title: 'Radiator & Cooling',
      description: 'Cooling system repair to prevent overheating',
      link: '/services/radiator-repair',
      color: 'bg-cyan-500'
    },
    {
      icon: FiBattery,
      title: 'Performance Upgrades',
      description: 'Tuning, exhaust, turbos - make real power',
      link: '/services/performance-upgrades',
      color: 'bg-orange-500'
    },
    {
      icon: FiActivity,
      title: 'Vehicle Diagnostics',
      description: 'Advanced diagnostic for check engine lights and issues',
      link: '/services/vehicle-diagnostics',
      color: 'bg-indigo-500'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen"
    >
      <Helmet>
        <title>Auto Repair Services | European Car Specialists | Paradise-Performance</title>
        <meta name="description" content="Complete auto repair services in Paradise, TX. European car specialists with expert technicians, quality parts, and warranty on all repairs." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white py-24">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative container mx-auto px-4">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Complete Auto Repair Services
            </h1>
            <p className="text-xl mb-8 text-gray-100">
              European Specialists • Full-Service Repair Shop
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Schedule Service
              </Link>
              <a
                href="tel:9403490038"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors"
              >
                Call (817) 382-2316
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From routine maintenance to complex repairs, we handle it all
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {allServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <Link
                  to={service.link}
                  className="block h-full bg-white rounded-lg shadow-lg hover:shadow-xl transition-all p-6 group"
                >
                  <div className={`w-12 h-12 ${service.color} text-white rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <service.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-primary-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600">
                    {service.description}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* European Specialists Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              European Car Specialists
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {serviceCategories.europeanRepair.brands.map((brand, index) => (
                <Link
                  key={index}
                  to={brand.path}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all p-6 text-center group"
                >
                  <h3 className="font-semibold group-hover:text-primary-600 transition-colors">
                    {brand.name}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-primary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why Choose Paradise-Performance?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                'European Car Specialists',
                'Full-Service Repair Shop',
                'Latest Diagnostic Equipment',
                '1 Year/12,000 Mile Warranty',
                'Quality OEM Parts',
                'Transparent Pricing',
              ].map((feature, index) => (
                <div key={index} className="flex items-center">
                  <FiCheckCircle className="text-primary-600 mr-3 flex-shrink-0 w-6 h-6" />
                  <span className="text-lg">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Schedule Your Service?
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            Expert technicians, quality parts, and guaranteed work
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Schedule Service Online
            </Link>
            <div className="text-center">
              <p className="mb-2">Or Call Us Now</p>
              <a
                href="tel:9403490038"
                className="text-2xl font-bold hover:underline"
              >
                (817) 382-2316
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* Vehicle Inventory Links */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <RelatedInventory limit={4} title="Vehicles For Sale" />
      </div>
    </motion.div>

  );
};

export default ServicesIndex;
